import { useEffect } from "react";
import { pageMeta } from "@/marketing/catalog";
import { structuredDataForPage } from "@/marketing/seo/schema";

const ORIGIN = "https://raqoz.com";
const OG_IMAGE = `${ORIGIN}/og-raqoz.png`;

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) { element = document.createElement("meta"); document.head.appendChild(element); }
  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
}

export function Seo({ path }: { path: string }) {
  useEffect(() => {
    const meta = pageMeta(path);
    const canonicalPath = meta.canonicalPath || meta.path;
    const canonicalUrl = `${ORIGIN}${canonicalPath === "/" ? "" : canonicalPath}`;
    const isArticle = meta.path.startsWith("/guides/") || meta.path.startsWith("/compare/") || meta.path === "/editorial-policy";
    const production = import.meta.env.PROD || import.meta.env.VITE_RAQOZ_ENV === "production";
    document.title = meta.title;
    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: production ? meta.robots : "noindex,nofollow" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: isArticle ? "article" : "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: OG_IMAGE });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: "RAQOZ procurement software — every purchase, accountable." });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: OG_IMAGE });
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = canonicalUrl;
    let schema = document.head.querySelector<HTMLScriptElement>('script[data-raqoz-schema="page"]');
    if (!schema) { schema = document.createElement("script"); schema.type = "application/ld+json"; schema.dataset.raqozSchema = "page"; document.head.appendChild(schema); }
    schema.text = JSON.stringify(structuredDataForPage(meta));
  }, [path]);
  return null;
}
